// /scada/events — events & alarms (R3). Real monitored alerts (alert_log) joined
// with operator responses (alert_responses). Three views: Active, Sequence-of-events
// (flat chronological), Archive (resolved/historical). Falls back to mock if API down.
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useEffect, useMemo, useState } from 'react';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { api } from '@/services/api';
import { mockEvents, type SysEvent, type Severity } from '@/scada/mock/events';
import { useScadaStation } from '@/scada/useScadaStation';
import SegmentedTabs from '@/components/base/SegmentedTabs';
import ActionButton from '@/components/base/ActionButton';
import { Download } from 'lucide-react';

const SEV: Record<Severity, { c: string; icon: string; label: string }> = {
  // Категорії — словником диспетчерів (зустріч 16.10): критичні,
  // попереджувальні, інформаційні.
  alarm: { c: scada.state.alarm, icon: 'ri-alarm-warning-line', get label() { return i18n.t('ak_scada_critical'); } },
  warning: { c: scada.state.warning, icon: 'ri-error-warning-line', get label() { return i18n.t('ak_scada_warning_2'); } },
  info: { c: scada.state.curtailed, icon: 'ri-information-line', get label() { return i18n.t('ak_scada_informational'); } },
};
// Тексти подій приходять з бекенду українською; для відомих шаблонів показуємо переклад,
// решта (технічні коди, назви активів) лишається як є.
const evMsg = (m: string): string => {
  const map: Record<string, string> = {
    'Повна втрата генерації': i18n.t('ak_ev_total_loss_of_generation'),
    'Відсутній звʼязок (колектор мовчить)': i18n.t('ak_ev_no_link_collector_is_silent'),
    'Часткова втрата — інвертори offline': i18n.t('ak_ev_partial_loss_inverters_offline'),
  };
  return map[m] ?? m;
};
const evStation = (n: string): string => (n === 'Портфель' ? i18n.t('ak_ev_portfolio') : n);
const fmt = (s: string) => new Date(s).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });

interface ApiEvent {
  id: string; ts: string; severity: Severity; stationName: string; message: string;
  status: 'active' | 'acknowledged' | 'cleared'; respBy: string | null;
  respLabel: string | null; respAt: string | null;
}

function Row({ e }: { e: SysEvent }) {
  const s = SEV[e.severity];
  return (
    <div className="flex items-center gap-3" style={{ padding: '8px 0', borderBottom: `1px solid ${scada.border}` }}>
      <i className={s.icon} style={{ color: s.c, fontSize: 16 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: scada.text, fontSize: 13 }}>
          {evStation(e.stationName)}{e.device ? ` · ${e.device}` : ''} — {evMsg(e.message)}
        </div>
        <div style={{ color: scada.textMuted, fontSize: 11 }}>
          {s.label} · {fmt(e.ts)}
          {e.ackBy ? i18n.t('ak_scada_replied', { n: e.ackBy }) : ''}
          {!e.ackBy && e.status === 'active' ? i18n.t('ak_scada_awaiting_response') : ''}
        </div>
      </div>
      {e.status === 'cleared' && <span style={{ color: scada.state.curtailed, fontSize: 11 }}>{i18n.t('ak_scada_cleared')}</span>}
      {e.status === 'acknowledged' && <span style={{ color: scada.state.warning, fontSize: 11 }}>{i18n.t('ak_scada_in_progress')}</span>}
    </div>
  );
}

export default function ScadaEventsPage() {
  useTranslation();
  const station = useScadaStation();
  const [allEvents, setAllEvents] = useState<SysEvent[]>([]);
  const [view, setView] = useState<'active' | 'soe' | 'archive'>('active');
  const [sev, setSev] = useState<'all' | Severity>('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const data = await api.get<{ events: ApiEvent[] }>('/scada/events');
        if (!alive) return;
        const mapped: SysEvent[] = (data.events || []).map((e) => ({
          id: e.id,
          ts: e.ts,
          type: 'manual',
          severity: e.severity,
          stationId: '',
          stationName: e.stationName,
          message: e.message,
          status: e.status,
          ackBy: e.respBy ? `${e.respBy}${e.respLabel ? ` · ${e.respLabel}` : ''}` : undefined,
        }));
        setAllEvents(mapped);
      } catch {
        if (alive) setAllEvents(mockEvents());
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Стрічка звужена до СЕС із глобального селектора. Події приходять із
  // назвою станції (id у відповіді API немає), тож матчимо за назвою.
  const events = useMemo(() => allEvents.filter((e) => station.matchesName(e.stationName)), [allEvents, station]);
  const shown = useMemo(() => (sev === 'all' ? events : events.filter((e) => e.severity === sev)), [events, sev]);
  const active = useMemo(() => shown.filter((e) => e.status === 'active' || e.status === 'acknowledged'), [shown]);
  const archive = useMemo(() => shown.filter((e) => e.status === 'cleared'), [shown]);
  const soe = useMemo(() => [...shown].sort((a, b) => +new Date(b.ts) - +new Date(a.ts)), [shown]);

  const activeAlarmCount = active.filter((e) => e.severity === 'alarm' && e.status === 'active').length;

  // Перемикач подань — спільний SegmentedTabs (варіант outline), той самий,
  // що й навігація розділів SCADA: однакова висота, обводка й кегль.
  // Було: самописні кнопки з брендовою червоною заливкою й контуром
  // (scada.accent) — §3 лишає червоний за реальною помилкою, а не за
  // активним станом контрола.
  const sevCount = (s: Severity) => events.filter((e) => e.severity === s).length;
  const sevTabs = [
    { key: 'all', label: i18n.t('ak_scada_all_categories') },
    { key: 'alarm', label: i18n.t('ak_scada_critical_2', { n: sevCount('alarm') }) },
    { key: 'warning', label: i18n.t('ak_scada_warnings', { n: sevCount('warning') }) },
    { key: 'info', label: i18n.t('ak_scada_informational_2', { n: sevCount('info') }) },
  ];

  // Експорт поточного подання з урахуванням фільтра — для акта чи передачі
  // зміни. Станційна EMS цього не вміє («тут немає», зустріч 16.10).
  // Роздільник «;» і BOM — щоб Excel з укр. локаллю відкрив без майстра.
  const current = view === 'active' ? active : view === 'archive' ? archive : soe;
  const exportCsv = () => {
    const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const STATUS: Record<string, string> = { active: i18n.t('ak_scada_active_2'), acknowledged: i18n.t('ak_scada_in_progress'), cleared: i18n.t('ak_scada_cleared') };
    const lines = [
      [i18n.t('ak_scada_time'), i18n.t('ak_scada_station'), i18n.t('ak_scada_device'), i18n.t('ak_scada_category'), i18n.t('ak_scada_event'), i18n.t('ak_scada_status'), i18n.t('ak_scada_response')].map(esc).join(';'),
      ...current.map((e) => [
        new Date(e.ts).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'uk-UA'), evStation(e.stationName), e.device ?? '', SEV[e.severity].label,
        evMsg(e.message), STATUS[e.status] ?? e.status, e.ackBy ?? '',
      ].map((v) => esc(String(v))).join(';')),
    ];
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `podii_${view}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  const viewTabs = [
    { key: 'active', label: i18n.t('ak_scada_active', { n: active.length }) },
    { key: 'soe', label: i18n.t('ak_scada_sequence_of_events', { n: soe.length }) },
    { key: 'archive', label: i18n.t('ak_scada_archive', { n: archive.length }) },
  ];

  return (
    <>
      <div className="flex flex-wrap items-center" style={{ marginBottom: 16, gap: 10 }}>
        <SegmentedTabs
          variant="outline"
          items={viewTabs}
          activeKey={view}
          onSelect={(k) => setView(k as typeof view)}
        />
        <SegmentedTabs
          variant="outline"
          items={sevTabs}
          activeKey={sev}
          onSelect={(k) => setSev(k as typeof sev)}
        />
        <ActionButton tone="ghost" Icon={Download} onClick={exportCsv} disabled={current.length === 0}>
          {i18n.t('ak_scada_export_csv')}
        </ActionButton>
      </div>

      {view === 'active' && (
        <SectionCard title={i18n.t('ak_scada_active_events_and_alarms')} subtitle={i18n.t('ak_scada_open_alarms_source_generation_monitori', { n: activeAlarmCount })}>
          {active.map((e) => <Row key={e.id} e={e} />)}
          {!loading && active.length === 0 && <span style={{ color: scada.textMuted, fontSize: 13 }}>{i18n.t('ak_scada_no_active_events')}</span>}
          {loading && <span style={{ color: scada.textMuted, fontSize: 13 }}>{i18n.t('ak_scada_loading')}</span>}
        </SectionCard>
      )}

      {view === 'soe' && (
        <SectionCard title={i18n.t('ak_scada_sequence_of_events_chronology')} subtitle={i18n.t('ak_scada_all_events_for_14_days_in_chronologica')}>
          {soe.map((e) => <Row key={e.id} e={e} />)}
          {!loading && soe.length === 0 && <span style={{ color: scada.textMuted, fontSize: 13 }}>{i18n.t('ak_scada_no_events')}</span>}
        </SectionCard>
      )}

      {view === 'archive' && (
        <SectionCard title={i18n.t('ak_scada_event_archive')} subtitle={i18n.t('ak_scada_cleared_and_historical_records')}>
          {archive.map((e) => <Row key={e.id} e={e} />)}
          {!loading && archive.length === 0 && <span style={{ color: scada.textMuted, fontSize: 13 }}>{i18n.t('ak_scada_the_archive_is_empty')}</span>}
        </SectionCard>
      )}
    </>
  );
}
